import NextHead from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState } from 'react';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { addPaywallSchema } from 'utils/paywallUtils';
import { formatAmpersand } from 'utils/miscUtils/myFavorites';
import { PageType } from 'interfaces/content/articles/Post';
import type { PageAdConfig } from 'interfaces/ads/Ad';
import { addStnMetaSchema } from 'utils/metaDataUtils/setJsonLdSchema';
import { UserContext } from 'providers/UserProvider';
import { commonColors } from 'styles/colors';
import { objectToLiteralString } from 'utils/stringUtils';
import type CustomWindow from 'interfaces/Utils';
import customOneTrustLocation from 'utils/miscUtils/customOneTrustLocation';
import Cookies from 'js-cookie';
import type { SubscriberContext } from 'services/Subscriber';

export interface HeadProps {
  authors?: string;
  databaseId: string;
  dateGmt: string | null;
  description: string;
  faqSchema: string;
  hasStnVideo: boolean;
  hasVanityUrl?: boolean;
  isBranded?: boolean;
  image: string;
  imageTwitter?: string;
  jsonLdSchema: string;
  keywords: string;
  link: string;
  pageAdConfig?: PageAdConfig;
  pianoPageConfig: PianoPageConfig;
  premiumCategory: string;
  primarySection: string;
  socialDescription: string;
  socialTitle: string;
  tagNames: string[];
  title: string;
  pageType: PageType;
  canonicalUrl: string | null;
  redVenture?: boolean;
  siteName?: string;
  subDomain?: string;
  useBinanceWidget?: boolean;
  rssFeedLink: string | null;
  widgetCss?: string | null;
  prefixedDatabaseId?: string | null;
  noIndex?: boolean | null;
  templateType?: string;
}
interface OneTrustUser {
  dataSubjectParams: {
    isAnonymous: boolean; // Tells OneTrust whether the user is logged in or not; this is handled automatically
    id?: string;
    token?: string;
  };
  geolocationResponse?: {
    stateCode: string;
    countryCode: string;
    regionCode?: string;
  };
}

declare let window: CustomWindow;
const Head = (props: HeadProps) => {
  const router = useRouter();
  const referrer = useRef<string>('');
  const pageTitle = useRef<string>('');
  const queryParamsRef = useRef<string>('');
  const { userState: user, setHasConsent, setPersonalizedAds, setPerformanceTracking } = useContext(UserContext);
  const [loadOneTrust, setLoadOneTrust] = useState(false);
  // Start with default case for non-logged-in user
  const [oneTrustUserObj, setOneTrustUserObj] = useState<OneTrustUser>({
    dataSubjectParams: {
      isAnonymous: true,
    },
  });

  const {
    authors,
    pageType,
    description,
    faqSchema,
    hasVanityUrl,
    isBranded,
    image,
    imageTwitter,
    jsonLdSchema,
    keywords,
    widgetCss,
    link,
    noIndex,
    pageAdConfig,
    pianoPageConfig,
    socialDescription,
    socialTitle,
    title,
    canonicalUrl,
    redVenture,
    siteName = 'Fortune',
    useBinanceWidget,
    rssFeedLink = '',
  } = props;

  let initialCanonical;

  if (canonicalUrl && canonicalUrl !== '') {
    initialCanonical = canonicalUrl;
  } else {
    initialCanonical = link;
  }
  const [canonical, setCanonical] = useState(initialCanonical);

  // References to piano user handler and OneTrust mount state
  // Used for a watchdog timer that mounts OneTrust if piano doesn't load in time
  // This ensures consent management is mounted if piano gets stuck
  // (check fetchToken call below)
  const userContextRef = useRef<SubscriberContext>(user);
  userContextRef.current = user;
  const hasInitializedOneTrust = useRef<boolean>(loadOneTrust);
  hasInitializedOneTrust.current = loadOneTrust;

  useEffect(() => {
    if (hasVanityUrl) {
      setCanonical(window.location.href);
    }
  }, [hasVanityUrl]);

  useEffect(() => {
    queryParamsRef.current = window.location.search;
    referrer.current = document.referrer;
    pageTitle.current = document.title;
  }, []);

  useEffect(() => {
    const fetchToken = async () => {
      if (user.isLoggedIn && user.pianoUid) {
        const response = await fetch(`${process.env.SITE_BASE_URL}/api/getJwtToken/${user.pianoUid}/`);
        if (!response.ok) {
          throw new Error('Could not get piano user JWT');
        }
        const userToken = (await response.json()) as string;
        setOneTrustUserObj({
          dataSubjectParams: {
            isAnonymous: false,
            id: user.pianoUid,
            token: userToken,
          },
        });
        setLoadOneTrust(true);
      } else if (process.env.ENVIRONMENT === 'qa' && router.query.customGeolocation) {
        Cookies.set('customGeolocation', router.query.customGeolocation as string);
        const geolocationByQuery = customOneTrustLocation(router.query.customGeolocation as string);
        setOneTrustUserObj({
          dataSubjectParams: {
            isAnonymous: true,
          },
          geolocationResponse: geolocationByQuery,
        });
      } else if (process.env.ENVIRONMENT === 'qa' && Cookies.get('customGeolocation')) {
        const geolocationByCookie = customOneTrustLocation(Cookies.get('customGeolocation') as string);
        setOneTrustUserObj({
          dataSubjectParams: {
            isAnonymous: true,
          },
          geolocationResponse: geolocationByCookie,
        });
      }
    };

    if (user.isLoggedIn !== null) {
      // Attempt to load user data if the user service has loaded
      fetchToken()
        .catch((error) => {
          console.error('Error during fetchToken execution, setting user object as anonymous', error);
        })
        // We only require OneTrust to *wait* for the user object in order to sync
        // Load OneTrust once fetching ends, regardless of outcome
        .finally(() => {
          setLoadOneTrust(true);
        });
    } else {
      // Otherwise, set a watchdog timer
      // When the timer expires, if the user service is not functioning as expected
      // (null value on login flag), mount OneTrust without a user object
      // This ensures we load OneTrust regardless of piano working or not
      // This mechanism is required as in normal circumstances, we need to wait for piano to get
      // the user ID so that OneTrust can fetch their preferences
      // However, if piano gets stuck, we don't want to run our website without consent management
      // (which would incur no tracking, no ads etc)
      setTimeout(() => {
        // This side-effect might've already ran again and mounted OneTrust in the meantime
        // Ensure we only run the code if OneTrust has not, in fact, been set to load yet
        if (!hasInitializedOneTrust.current) {
          // If the user context still shows a null value on its login flag, mount OneTrust directly
          if (userContextRef.current.isLoggedIn === null) {
            console.error(
              'Could not load user identity provider in time, mounting consent provider with anonymous user object',
            );
            setLoadOneTrust(true);
          }
        }
      }, 5000);
    }
  }, [user.isLoggedIn, user.pianoUid]);

  useEffect(() => {
    if (loadOneTrust) {
      window.OptanonWrapper = () => {
        const requiredFlags = ['C0001'];
        const performanceFlags = ['C0002', 'C0003'];
        const personalizationFlags = ['C0004', 'C0005'];
        const activeGroups = window.OnetrustActiveGroups?.split(',') || [];
        if (activeGroups.length > 0) {
          const hasRequiredFlags = requiredFlags.every((flag) => activeGroups.includes(flag));
          const hasPersonalizedAdsFlags = personalizationFlags.every((flag) => activeGroups.includes(flag));
          const hasPerformanceFlags = performanceFlags.every((flag) => activeGroups.includes(flag));
          setHasConsent(hasRequiredFlags);
          setPerformanceTracking(hasPerformanceFlags);
          setPersonalizedAds(hasPersonalizedAdsFlags);
        }
      };
      window.OptanonWrapper();
    }
  }, [loadOneTrust]);

  let jsonLdSchemaFinal;
  if (
    pianoPageConfig &&
    [
      PageType.ARTICLE,
      PageType.COMPANY,
      PageType.LONGFORM,
      PageType.FRANCHISE_LIST,
      PageType.FRANCHISE_LIST_ITEM,
      PageType.FRANCHISE_LIST_ITEM_COMPANY,
      PageType.FRANCHISE_LIST_SEARCH,
    ].includes(pianoPageConfig.pageType)
  ) {
    jsonLdSchemaFinal = addPaywallSchema(jsonLdSchema, pianoPageConfig);
  } else {
    jsonLdSchemaFinal = jsonLdSchema;
  }
  const parsedSchema = jsonLdSchemaFinal ? JSON.parse(jsonLdSchemaFinal) : [];
  // Only add STN Meta if the schema exists and has stnMeta property on any of its nodes
  const hasStnMeta = !Array.isArray(parsedSchema)
    ? parsedSchema.stnMeta
    : parsedSchema.some((schema) => schema.stnMeta);
  if (hasStnMeta && pageAdConfig) {
    jsonLdSchemaFinal = addStnMetaSchema(jsonLdSchemaFinal, pageAdConfig);
  }

  function getRobotsContent() {
    const robotsMetaContent: string[] = [];

    if ([PageType.EVERGREEN, PageType.ARTICLE].includes(pageType)) {
      robotsMetaContent.push('max-image-preview:large');
    }

    return robotsMetaContent.toString().replaceAll(',', ', ');
  }

  function setUserObjScriptVal(obj: OneTrustUser | null | undefined) {
    if (obj) {
      return objectToLiteralString(obj);
    }
    return "''";
  }

  const dataDomainScriptId = process.env.ONE_TRUST_DOMAIN_ID;
  const oneTrustScript = `https://cdn.cookielaw.org/consent/${dataDomainScriptId}/otSDKStub.js`;
  const shouldHaveAuthorMeta = authors && [PageType.ARTICLE, PageType.EVERGREEN, PageType.LONGFORM].includes(pageType);

  function searchQueryCheck() {
    const searchQueryRoutes = [
      '/ranking/[franchiseSlug]/search',
      '/ranking-v2/[franchiseSlug]',
      '/ranking/[franchiseSlug]',
    ];

    if (searchQueryRoutes.includes(router.route) && Object.keys(router.query).length > 1) {
      return true;
    }
    return false;
  }

  return (
    <NextHead>
      <title>{formatAmpersand(title)}</title>
      <meta
        name='msapplication-TileColor'
        content='#ffc40d'
      />
      <meta
        name='theme-color'
        content={commonColors.surfaceWhite}
      />
      <meta
        name='description'
        content={description}
      />
      {shouldHaveAuthorMeta && (
        <meta
          name='author'
          content={authors}
        />
      )}
      <meta
        name='keywords'
        content={keywords}
      />
      <meta
        property='og:url'
        content={link}
      />
      <meta
        property='og:type'
        content='article'
      />
      <meta
        property='og:title'
        content={socialTitle}
      />
      <meta
        property='og:description'
        content={socialDescription}
      />
      <meta
        property='og:site_name'
        content={siteName}
      />
      <meta
        property='og:image'
        content={image}
      />
      <meta
        property='og:width'
        content='1024'
      />
      <meta
        property='og:height'
        content='683'
      />
      <meta
        name='twitter:card'
        content='summary_large_image'
      />
      <meta
        name='twitter:title'
        content={socialTitle}
      />
      <meta
        name='twitter:description'
        content={socialDescription}
      />
      <meta
        name='twitter:image'
        content={imageTwitter || image}
      />
      <meta
        name='twitter:url'
        content={link}
      />
      <meta
        property='fb:pages'
        content='56501027948'
      />
      <meta
        name='robots'
        content={getRobotsContent()}
      />
      {router.route === '/advanced-search' && (
        <meta
          name='robots'
          content='noindex, nofollow'
        />
      )}
      {noIndex === true && (
        <meta
          name='robots'
          content='noindex'
        />
      )}
      {searchQueryCheck() && (
        <meta
          name='robots'
          content='noindex'
        />
      )}
      {widgetCss && (
        <style type='text/css'>
          {`
            ${widgetCss}
          `}
        </style>
      )}
      {/* <link
        rel='preconnect'
        href='https://www.googletagmanager.com'
        crossOrigin=''
      /> */}
      <link
        rel='preconnect'
        href='https://www.google-analytics.com'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://www.googletagservices.com'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://ad.doubleclick.net'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://a.pub.network/'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://b.pub.network/'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://c.pub.network/'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://d.pub.network/'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://c.amazon-adsystem.com'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://s.amazon-adsystem.com'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://btloader.com/'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://api.btloader.com/'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://cdn.confiant-integrations.net'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://static.doubleclick.net'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://securepubads.g.doubleclick.net'
        crossOrigin=''
      />
      <link
        rel='icon'
        href='/icons/favicons/favicon.ico'
      />
      <link
        rel='apple-touch-icon'
        type='image/png'
        href='/icons/favicons/apple-touch-icon.png'
        sizes='193x193'
      />
      <link
        rel='canonical'
        href={canonical}
      />
      {redVenture && (
        <link
          rel='preconnect'
          href='https://static.myfinance.com'
          crossOrigin=''
        />
      )}
      {useBinanceWidget && (
        <link
          rel='preconnect'
          href='https://public.bnbstatic.com/'
          crossOrigin=''
        />
      )}
      {rssFeedLink && (
        <link
          rel='alternate'
          type='application/rss+xml'
          title='Fortune RSS'
          href={rssFeedLink}
        />
      )}
      {faqSchema && (
        <script
          id='faq-json-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: faqSchema }}
        />
      )}
      {jsonLdSchemaFinal && (
        <script
          id='json-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: jsonLdSchemaFinal }}
        />
      )}

      {loadOneTrust && (
        <>
          <script
            id='OneTrustUserObject'
            type='text/javascript'
            data-cy='one-trust-user'
          >
            var OneTrust = {setUserObjScriptVal(oneTrustUserObj)}
          </script>
          <script
            async
            src={oneTrustScript}
            data-document-language='true'
            type='text/javascript'
            data-domain-script={dataDomainScriptId}
            data-cy='one-trust-script'
          />
        </>
      )}

      {isBranded && (
        <script
          type='text/javascript'
          src='https://s.ntv.io/serve/load.js'
          data-ntv-article
          async
        ></script>
      )}

      <link
        rel='preconnect'
        href='https://dev.visualwebsiteoptimizer.com'
      />

      <link
        rel='dns-prefetch'
        href='https://c.amazon-adsystem.com'
      />
      <link
        rel='preconnect'
        href='https://c.amazon-adsystem.com'
        crossOrigin=''
      />
      <script
        async
        src='https://c.amazon-adsystem.com/aax2/apstag.js'
      />
    </NextHead>
  );
};

export default Head;
